import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { TrialNavBadge } from './TrialBanner';
import { Square, LogOut, Menu, X, User, LayoutDashboard, Map, FileText, CreditCard, Settings, BookOpen, Sparkles, Info } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getUsage } from '../lib/api';
import AuthModal from './AuthModal';

export function Navbar() {
  const { user, isAuthenticated, logout, openAuthModal, showAuthModal, authModalTab, closeAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      getUsage().then(res => {
        if (res.data.is_trial) {
          setTrialDaysLeft(res.data.days_left);
        }
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Public navigation links (visible to everyone)
  const publicLinks = [
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/blog', label: 'Blog' },
    { to: '/about', label: 'About' },
  ];

  // Authenticated app navigation
  const appLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/measure', label: 'Measure', icon: Map },
    { to: '/reports', label: 'Reports', icon: FileText },
    { to: '/pricing', label: 'Pricing', icon: CreditCard },
    { to: '/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="sticky top-0 z-50 bg-slate-950/70 backdrop-blur-xl border-b border-white/[0.06] transition-all duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2.5 group shrink-0">
              <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:shadow-orange-500/30 transition-shadow">
                <Square className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-white tracking-tight">
                SquareCount
              </span>
            </Link>

            {/* Center Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {isAuthenticated ? (
                // App navigation for logged-in users
                appLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        isActive(link.to)
                          ? 'bg-orange-500/15 text-white'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  );
                })
              ) : (
                // Public navigation for visitors
                publicLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      isActive(link.to)
                        ? 'text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))
              )}
            </div>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {trialDaysLeft !== null && (
                    <TrialNavBadge daysLeft={trialDaysLeft} />
                  )}
                  <div className="flex items-center gap-2.5 pl-3 border-l border-slate-800">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-2 ring-slate-800" />
                    ) : (
                      <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center ring-2 ring-slate-700">
                        <User className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                    <span className="text-slate-300 text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="text-slate-500 hover:text-slate-300 p-2 rounded-lg hover:bg-white/5 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openAuthModal('login')}
                    data-testid="login-btn"
                    className="text-slate-400 hover:text-white px-4 py-2 text-sm font-medium rounded-lg hover:bg-white/5 transition-colors duration-200"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => openAuthModal('register')}
                    data-testid="signup-btn"
                    className="btn-primary text-sm px-5 py-2"
                  >
                    Start Free
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-xl border border-slate-800 animate-slide-down">
            <div className="px-4 py-4 space-y-1">
              {isAuthenticated ? (
                <>
                  {/* User info */}
                  <div className="flex items-center gap-3 px-3 py-3 mb-2">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium text-sm">{user?.name}</p>
                      <p className="text-slate-500 text-xs">{user?.email}</p>
                    </div>
                  </div>
                  {appLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${
                          isActive(link.to)
                            ? 'bg-orange-500/15 text-orange-400'
                            : 'text-slate-300 hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {link.label}
                      </Link>
                    );
                  })}
                  <hr className="border-slate-800 my-3" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-300 w-full hover:bg-white/5"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  {/* Public links on mobile */}
                  {publicLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block px-3 py-3 rounded-lg text-sm font-medium ${
                        isActive(link.to)
                          ? 'text-white bg-white/5'
                          : 'text-slate-300 hover:bg-white/5'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <hr className="border-slate-800 my-3" />
                  <div className="space-y-2 pt-1">
                    <button
                      onClick={() => openAuthModal('login')}
                      className="w-full text-center px-4 py-3 rounded-lg text-sm font-medium text-slate-300 border border-slate-800 hover:bg-white/5 transition-colors"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => openAuthModal('register')}
                      className="w-full btn-primary text-sm py-3"
                    >
                      Start Free Trial
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={closeAuthModal} defaultTab={authModalTab} />
    </>
  );
}
